'use client';

import type { ProductDetailVersion } from 'src/types/product-rankings/product-rankings-ops';

import { Box, Stack, Tooltip, Typography } from '@mui/material';

import { RADIUS } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';

import { ymdToLong, ymdToShort } from '../utils/market-rankings-format';


/**
 * 상세페이지 **판 목록** — 왼쪽이 옛것, 오른쪽이 현재.
 *
 * 칸 하나가 판 하나다. 누르면 위쪽 미디어가 그 시점 모습으로 바뀐다.
 *
 * ## ⚠️ 「현재」는 `seq`가 가장 큰 칸이 아니다
 *
 * 상세 미디어가 옛 스냅샷에서 올라오는 경우가 있어(상위 N개만 확장 수집), 화면이 그리고
 * 있는 것이 최신 판이 아닐 수 있다. 그래서 **해시로 맞춘 `currentSeq`를 받아** 그 칸에
 * 「현재」를 붙인다 — `timeline`의 마지막 칸에 붙이면 거짓말이 된다.
 *
 * ## ⚠️ 칸에 **무엇이 달라졌는지는 적지 않는다**
 *
 * 수집기의 `changeType`(예: `'text-only'`)과 우리가 보관한 필드가 어긋나 — 그 값이 붙어도
 * 본문이 한 글자도 안 다른 판이 있다 — 증감 수치를 「이게 달라졌습니다」로 내놓을 근거가
 * 없다. **칸은 판을 가리키는 이름표일 뿐이고, 무엇이 달라졌는지는 사람이 판을 오가며 본다.**
 */
export default function DetailVersionTimeline({
  timeline,
  currentSeq,
  activeSeq,
  onSelect,
}: {
  /** 오름차순(옛것 → 최신) */
  timeline: ProductDetailVersion[];
  /** 지금 화면에 그려진 버전 */
  currentSeq: number | null;
  /** 사용자가 고른 버전. `null`이면 현재를 보고 있다 */
  activeSeq: number | null;
  onSelect: (seq: number | null) => void;
}) {
  if (timeline.length < 2) return null;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Iconify icon="solar:history-linear" width={16} sx={{ color: 'text.disabled' }} />
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          상세페이지 {timeline.length}판
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          · 누르면 그때 모습을 봅니다
        </Typography>
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        sx={{
          overflowX: 'auto',
          pb: 0.5,
          // ⚠️ 칸이 많으면 가로로 넘친다 — 줄바꿈이 아니라 «가로 스크롤»이다. 시간축이라
          // 두 줄로 접히면 왼쪽이 옛것이라는 규칙이 깨진다
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: RADIUS.lg },
        }}
      >
        {timeline.map((version) => {
          const seq = version.seq ?? 0;
          const isCurrent = currentSeq != null && seq === currentSeq;
          const selected = activeSeq == null ? isCurrent : seq === activeSeq;

          const hint = [
            `v${seq} · ${ymdToLong(version.firstDate)} 관측`,
            version.lastDate && version.lastDate !== version.firstDate
              ? `~ ${ymdToLong(version.lastDate)}`
              : '',
            version.assetCount != null ? `${version.assetCount}컷` : '',
          ]
            .filter(Boolean)
            .join(' · ');

          return (
            <Tooltip key={`${seq}-${version.hash}`} title={hint}>
              <Box
                component="button"
                type="button"
                onClick={() => onSelect(isCurrent ? null : seq)}
                sx={{
                  flexShrink: 0,
                  minWidth: 92,
                  px: 1,
                  py: 1,
                  border: '1px solid',
                  borderColor: selected ? 'primary.main' : 'divider',
                  bgcolor: selected ? 'primary.lighter' : 'common.white',
                  color: selected ? 'primary.dark' : 'text.secondary',
                  borderRadius: RADIUS.md,
                  cursor: 'pointer',
                  textAlign: 'left',
                  font: 'inherit',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography sx={{ typography: 'uiMeta', fontWeight: 600 }}>v{seq}</Typography>
                  {isCurrent ? (
                    <Typography sx={{ typography: 'uiLabel', color: 'success.dark' }}>
                      현재
                    </Typography>
                  ) : null}
                </Stack>
                <Typography sx={{ typography: 'uiMeta', fontWeight: 600 }}>
                  {ymdToShort(version.firstDate)}
                </Typography>
                {version.assetCount != null ? (
                  <Typography sx={{ typography: 'uiMeta', color: 'text.disabled' }}>
                    {version.assetCount}컷
                  </Typography>
                ) : null}
              </Box>
            </Tooltip>
          );
        })}
      </Stack>
    </Box>
  );
}
